import {
  ThemeButtonSchema, ThemeColorsSchema, ThemeFontsSchema, ThemeRadiusSchema, ThemeShadowsSchema, ThemeSpacingSchema,
  type ElementStylesDto, type StyleBlockDto, type StyleValueDto, type ThemeStylesDto
} from '@xshop/shared';

// The store's own design tokens, and the handful of shorthands the closed `STYLE_PROPERTIES` list does
// not carry. A style bound to a token follows the store when the palette is repainted; a literal stays
// exactly where the merchant put it, which is why every default in this catalog reaches for a token first.
//
// A token is spelled as the path into `ThemeStylesDto` — `colors.primary`, `radius.md` — because that is
// how the admin's Styles pane already addresses them, and because `md` alone would name two of them.

export type ColorToken = keyof ThemeStylesDto['colors'];
export type RadiusToken = keyof ThemeStylesDto['radius'];
export type SpacingToken = keyof ThemeStylesDto['spacing'];
export type FontToken = keyof ThemeStylesDto['fonts'];

// Binds a style to one of the store's colours, so repainting the palette repaints this element with it.
export const paint = (name: ColorToken): StyleValueDto => ({ token: `colors.${name}` });

// Binds a style to one of the store's four corner radii.
export const round = (name: RadiusToken): StyleValueDto => ({ token: `radius.${name}` });

// Binds a style to one step of the store's spacing scale.
export const space = (name: SpacingToken): StyleValueDto => ({ token: `spacing.${name}` });

// Binds a style to one of the store's typefaces.
export const typeface = (name: FontToken): StyleValueDto => ({ token: `fonts.${name}` });

/** One step of the theme's optional depth scale; a theme naming none keeps every element flat. */
export type ShadowToken = 'sm' | 'md' | 'lg';

// Binds a style to one step of the store's shadow scale, so retinting the depth system is one token edit.
export const shade = (name: ShadowToken): StyleValueDto => ({ token: `shadows.${name}` });

// Lists one flat group's tokens under the path a style value references them by.
const tokensOf = (group: string, keys: readonly string[]): readonly string[] => keys.map((key) => `${group}.${key}`);

// Lists the two button skins, which are the one nested group a theme's tokens carry.
const buttonTokens = (): readonly string[] =>
  ThemeButtonSchema.keyof().options.flatMap((key) => [`buttons.primary.${key}`, `buttons.secondary.${key}`]);

/**
 * Every token name a style value may reference, read off the token schemas themselves rather than
 * restated here — so a token added to `ThemeStylesSchema` is offered by the inspector without a second edit.
 */
export const STYLE_TOKENS: readonly string[] = [
  ...tokensOf('colors', ThemeColorsSchema.keyof().options),
  ...tokensOf('fonts', ThemeFontsSchema.keyof().options),
  ...tokensOf('radius', ThemeRadiusSchema.keyof().options),
  ...tokensOf('spacing', ThemeSpacingSchema.keyof().options),
  ...tokensOf('shadows', ThemeShadowsSchema.keyof().options),
  ...buttonTokens()
];

// Wraps one block of properties as the resting look at the design width, which is where an element starts.
export const look = (block: StyleBlockDto): ElementStylesDto => ({ base: { default: block } });

// Adds the pointer-over look to styles that already carry their resting one.
export const hover = (styles: ElementStylesDto, block: StyleBlockDto): ElementStylesDto =>
  ({ ...styles, base: { ...styles.base, hover: block } });

// Adds the keyboard-focus look, which is the state a form control cannot honestly ship without.
export const focus = (styles: ElementStylesDto, block: StyleBlockDto): ElementStylesDto =>
  ({ ...styles, base: { ...styles.base, focus: block } });

// Adds what changes on a phone, so a dropped element is legible at 390px without a second edit.
export const onPhone = (styles: ElementStylesDto, block: StyleBlockDto): ElementStylesDto =>
  ({ ...styles, mobile: { default: block } });

// Spells vertical and horizontal padding as the four properties the closed style list actually carries.
export const pad = (vertical: StyleValueDto, horizontal: StyleValueDto): StyleBlockDto =>
  ({ paddingTop: vertical, paddingBottom: vertical, paddingLeft: horizontal, paddingRight: horizontal });

// Spells one radius on all four corners, which the closed style list has no shorthand for either.
export const radius = (value: StyleValueDto): StyleBlockDto => ({
  borderTopLeftRadius: value, borderTopRightRadius: value,
  borderBottomRightRadius: value, borderBottomLeftRadius: value
});

// Spells a one-pixel outline in the store's own colour, since width, style and colour are three properties every time.
export const hairline = (color: StyleValueDto = paint('border')): StyleBlockDto =>
  ({ borderStyle: 'solid', borderWidth: '1px', borderColor: color });

// The vertical flow a box lays its children out in, which is what makes an empty container droppable.
export const stack = (gap: StyleValueDto): StyleBlockDto => ({ display: 'flex', flexDirection: 'column', gap });

// The horizontal flow a bar lays its children out in, centred and free to wrap onto a second line.
export const strip = (gap: StyleValueDto): StyleBlockDto =>
  ({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap });
